import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { User } from 'src/app/models/user.model';
import { MediaRecording } from 'src/app/models/media-recording.model';
import { resolveMediaRecordingDiarizationInterventions } from '../media-recording-diarization-page/media-recording-diarization-page.component';
import {
  applyMeetingRecordMediaRecordingPusherUpdate,
  buildYoutubeEmbedUrl,
  canRetryMediaRecordingTranscription,
  MediaRecordingUploadRetryContext,
  resolveMediaRecordingUploadRetryContext,
} from '../meeting-record.component';
import { ApiService } from 'src/app/services/api.service';
import { UtilsService } from 'src/app/services/utils.service';
import { RecordingService } from 'src/app/services/recording.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SafeUrlPipe } from 'src/app/pipes/safe-url.pipe';
import { PusherService } from 'src/app/services/pusher.service';
import { MediaRecordingStatusLabelPipe } from 'src/app/pipes/media-recording-status-label.pipe';
import { MediaRecordingServerProcessingLabelPipe } from 'src/app/pipes/media-recording-server-processing-label.pipe';
import { MediaRecordingProcessingOnServerPipe } from 'src/app/pipes/media-recording-processing-on-server.pipe';
import { MediaRecordingTranscriptionInFlightPipe } from 'src/app/pipes/media-recording-transcription-in-flight.pipe';
import { MediaRecordingTranscriptionStatusLabelPipe } from 'src/app/pipes/media-recording-transcription-status-label.pipe';
import { RecordingElapsedLabelPipe } from 'src/app/pipes/recording-elapsed-label.pipe';

export interface MeetingRecordMediaRecordingsDialogData {
  meeting_record_id: number;
  media_recordings: MediaRecording[];
  assistant_users?: User[];
  can_edit_diarization?: boolean;
  return_url?: string;
  meetingable_type?: string;
  meetingable_id?: number;
}

interface MediaRecordingListItem {
  recording: MediaRecording;
  upload_retry_context: MediaRecordingUploadRetryContext | null;
  can_discard_local_upload: boolean;
  show_retry_transcription_action: boolean;
  show_diarization_actions: boolean;
  supports_youtube_playback: boolean;
  youtube_page_url: string | null;
  youtube_embed_url: string | null;
  supports_local_playback: boolean;
  local_playback_kind: 'audio' | 'video' | null;
  playback_object_url: string | null;
  is_loading_playback: boolean;
  playback_error: string | null;
  can_delete: boolean;
}

@Component({
  selector: 'app-meeting-record-media-recordings-dialog',
  templateUrl: './meeting-record-media-recordings-dialog.component.html',
  styleUrls: ['./meeting-record-media-recordings-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    SafeUrlPipe,
    MediaRecordingStatusLabelPipe,
    MediaRecordingServerProcessingLabelPipe,
    MediaRecordingProcessingOnServerPipe,
    MediaRecordingTranscriptionInFlightPipe,
    MediaRecordingTranscriptionStatusLabelPipe,
    RecordingElapsedLabelPipe,
  ],
})
export class MeetingRecordMediaRecordingsDialogComponent implements OnInit, OnDestroy {
  private dialog_ref = inject(MatDialogRef<MeetingRecordMediaRecordingsDialogComponent>);
  private router = inject(Router);
  private data = inject<MeetingRecordMediaRecordingsDialogData>(MAT_DIALOG_DATA);
  private api = inject(ApiService);
  private utils = inject(UtilsService);
  private recording_service = inject(RecordingService);
  private permissions = inject(PermissionsService);
  private user_service = inject(UserService);
  private snack_bar = inject(MatSnackBar);
  private change_detector_ref = inject(ChangeDetectorRef);
  private destroy_ref = inject(DestroyRef);
  private pusher = inject(PusherService);

  protected recording_items: MediaRecordingListItem[] = [];
  protected retrying_upload_recording_id: number | null = null;
  protected discarding_upload_recording_id: number | null = null;
  protected deleting_recording_id: number | null = null;

  private meeting_record_pusher_unsubscribe: (() => void) | null = null;

  ngOnInit(): void {
    this.setRecordingItems(this.data.media_recordings);
    this.subscribeToMeetingRecordMediaRecordingsChannel();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromMeetingRecordMediaRecordingsChannel();
    this.revokePlaybackObjectUrls();
  }

  close(): void {
    this.dialog_ref.close();
  }

  async retryRecordingUpload(item: MediaRecordingListItem): Promise<void> {
    if (!item.upload_retry_context?.can_retry) {
      this.snack_bar.open(item.upload_retry_context?.failure_summary ?? 'No se puede reintentar esta grabación.', undefined, {
        duration: 6000,
      });

      return;
    }

    const server_recording = this.data.media_recordings.find(recording => recording.id === item.recording.id) ?? null;

    this.retrying_upload_recording_id = item.recording.id;
    this.change_detector_ref.markForCheck();

    try {
      const retry_context = await this.recording_service.retryMediaRecordingUploadHybrid(item.recording.client_local_id, server_recording);

      this.snack_bar.open(retry_context.retry_summary ?? 'Reintento iniciado.', undefined, { duration: 6000 });
      this.refreshMediaRecordings();
    } catch (error: unknown) {
      const error_message = error instanceof Error ? error.message : 'No se pudo reintentar la subida.';

      this.snack_bar.open(error_message, undefined, { duration: 6000 });
    } finally {
      this.retrying_upload_recording_id = null;
      this.change_detector_ref.markForCheck();
    }
  }

  async discardFailedRecordingUpload(item: MediaRecordingListItem): Promise<void> {
    this.discarding_upload_recording_id = item.recording.id;
    this.change_detector_ref.markForCheck();

    try {
      const discarded = await this.recording_service.discardFailedOrPendingUpload(item.recording.client_local_id);

      if (discarded) {
        this.snack_bar.open('Subida descartada. Ya puedes iniciar una nueva grabación.', undefined, { duration: 5000 });
        this.refreshMediaRecordings();
      }
    } finally {
      this.discarding_upload_recording_id = null;
      this.change_detector_ref.markForCheck();
    }
  }

  downloadRecordingAudio(recording_id: number): void {
    this.api
      .downloadMediaRecordingAudio(recording_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(response => {
        this.utils.downloadPdfFromBlobResponse(response);
      });
  }

  openDiarizationEditor(recording_id: number): void {
    const query_params: Record<string, string> = {};

    if (this.data.return_url) {
      query_params.return_url = this.data.return_url;
    }

    if (this.data.can_edit_diarization === true) {
      query_params.can_edit = '1';
    }

    void this.router.navigate(
      ['/employees', 'meeting-records', this.data.meeting_record_id, 'media-recordings', recording_id, 'diarization'],
      { queryParams: query_params }
    );

    this.dialog_ref.close();
  }

  retryTranscription(recording_id: number): void {
    this.api
      .retryMediaRecordingTranscription(recording_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => {
          this.snack_bar.open('Transcripción en cola. Puede tardar unos minutos.', undefined, { duration: 4000 });
          this.refreshMediaRecordings();
        },
        error: (error: { error?: { message?: string } }) => {
          const error_message = error?.error?.message ?? 'No se pudo iniciar la transcripción.';
          this.snack_bar.open(error_message, undefined, { duration: 6000 });
        },
      });
  }

  deleteRecording(item: MediaRecordingListItem): void {
    if (!item.can_delete) {
      return;
    }

    const has_youtube_video = item.supports_youtube_playback;
    const confirmation_message = has_youtube_video
      ? '¿Eliminar esta grabación? Se borrarán los archivos del servidor y el vídeo de YouTube.'
      : '¿Eliminar esta grabación? Se borrarán los archivos del servidor.';

    if (!confirm(confirmation_message)) {
      return;
    }

    this.deleting_recording_id = item.recording.id;
    this.change_detector_ref.markForCheck();

    this.api
      .deleteMediaRecording(item.recording.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => {
          void this.recording_service
            .clearLocalRecordingUploadData(item.recording.client_local_id)
            .catch(() => undefined)
            .finally(() => {
              this.snack_bar.open('Grabación eliminada.', undefined, { duration: 4000 });
              this.deleting_recording_id = null;
              this.refreshMediaRecordings();
            });
        },
        error: (error: { error?: { message?: string } }) => {
          const error_message = error?.error?.message ?? 'No se pudo eliminar la grabación.';
          this.snack_bar.open(error_message, undefined, { duration: 6000 });
          this.deleting_recording_id = null;
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private refreshMediaRecordings(): void {
    this.api
      .getMeetingRecordMediaRecordings(this.data.meeting_record_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(recordings => {
        this.data.media_recordings = recordings;
        this.setRecordingItems(recordings);
      });
  }

  private subscribeToMeetingRecordMediaRecordingsChannel(): void {
    if (this.meeting_record_pusher_unsubscribe !== null) {
      return;
    }

    this.meeting_record_pusher_unsubscribe = this.pusher.subscribeToMeetingRecordChannel(this.data.meeting_record_id, update => {
      this.data.media_recordings = applyMeetingRecordMediaRecordingPusherUpdate(this.data.media_recordings, update);
      this.setRecordingItems(this.data.media_recordings);

      const should_refetch_full_recordings = update.transcription_status === 'completed' || update.recording_status === 'completed';

      if (should_refetch_full_recordings) {
        this.refreshMediaRecordings();
      }
    });
  }

  private unsubscribeFromMeetingRecordMediaRecordingsChannel(): void {
    if (this.meeting_record_pusher_unsubscribe === null) {
      return;
    }

    this.meeting_record_pusher_unsubscribe();
    this.meeting_record_pusher_unsubscribe = null;
  }

  private setRecordingItems(recordings: MediaRecording[]): void {
    this.revokePlaybackObjectUrls();
    this.recording_items = recordings.map(recording => this.serializeRecording(recording));
    this.change_detector_ref.markForCheck();

    for (const item of this.recording_items) {
      if (item.supports_local_playback) {
        this.loadLocalPlayback(item);
      }
    }
  }

  private serializeRecording(recording: MediaRecording): MediaRecordingListItem {
    const supports_youtube_playback =
      recording.media_type === 'video' && recording.youtube_video_id !== null && recording.youtube_video_id !== '';
    const supports_local_playback = this.canStreamLocalMedia(recording);
    const local_playback_kind = supports_local_playback ? this.resolveLocalPlaybackKind(recording) : null;
    const has_local_retry_data = this.recording_service.hasLocalRetryDataInQueue(recording.client_local_id);

    return {
      recording,
      upload_retry_context: resolveMediaRecordingUploadRetryContext(recording, has_local_retry_data),
      can_discard_local_upload: has_local_retry_data,
      show_retry_transcription_action: canRetryMediaRecordingTranscription(recording),
      show_diarization_actions:
        recording.recording_status === 'completed' &&
        recording.transcription_status === 'completed' &&
        resolveMediaRecordingDiarizationInterventions(recording).length > 0,
      supports_youtube_playback,
      youtube_page_url: recording.youtube_url,
      youtube_embed_url: supports_youtube_playback ? buildYoutubeEmbedUrl(recording.youtube_video_id!) : null,
      supports_local_playback,
      local_playback_kind,
      playback_object_url: null,
      is_loading_playback: false,
      playback_error: null,
      can_delete: this.canDeleteRecording(recording),
    };
  }

  private canDeleteRecording(recording: MediaRecording): boolean {
    const current_user = this.user_service.getCurrentUser();

    if (recording.created_by_user_id === current_user.id) {
      return true;
    }

    if (this.permissions.isAdmin() || this.permissions.validateDepartment(['direction', 'rrhh'])) {
      return true;
    }

    const meetingable_type = this.data.meetingable_type;
    const meetingable_id = this.data.meetingable_id;

    if (meetingable_type === 'App\\Models\\CompanyProduct' && meetingable_id !== undefined && meetingable_id !== null) {
      return (
        this.permissions.isBisualProductionTeamInCompanyProductAsTrio(String(meetingable_id)) ||
        this.permissions.isAccountManagerInCompanyProduct(String(meetingable_id))
      );
    }

    return false;
  }

  private canStreamLocalMedia(recording: MediaRecording): boolean {
    const has_playable_status = recording.recording_status === 'completed' || recording.recording_status === 'upload_failed';

    if (!has_playable_status || recording.storage_path === null) {
      return false;
    }

    const local_playback_kind = this.resolveLocalPlaybackKind(recording);

    if (local_playback_kind === null) {
      return false;
    }

    const has_youtube_playback =
      recording.media_type === 'video' && recording.youtube_video_id !== null && recording.youtube_video_id !== '';

    if (has_youtube_playback) {
      return false;
    }

    return true;
  }

  private resolveLocalPlaybackKind(recording: MediaRecording): 'audio' | 'video' | null {
    const mime_type = recording.mime_type ?? '';

    if (mime_type.startsWith('audio/') || recording.media_type === 'audio') {
      return 'audio';
    }

    const is_video_file = mime_type.startsWith('video/') || recording.storage_path?.endsWith('.mp4') === true;

    if (is_video_file) {
      return 'video';
    }

    if (recording.media_type === 'video' && recording.recording_status === 'upload_failed') {
      return 'video';
    }

    return null;
  }

  private loadLocalPlayback(item: MediaRecordingListItem): void {
    item.is_loading_playback = true;
    item.playback_error = null;
    this.change_detector_ref.markForCheck();

    this.api
      .streamMediaRecording(item.recording.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: blob => {
          item.playback_object_url = URL.createObjectURL(blob);
          item.is_loading_playback = false;
          this.change_detector_ref.markForCheck();
        },
        error: () => {
          item.is_loading_playback = false;
          item.playback_error = 'No se pudo cargar el archivo para reproducir.';
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private revokePlaybackObjectUrls(): void {
    for (const item of this.recording_items) {
      if (item.playback_object_url !== null) {
        URL.revokeObjectURL(item.playback_object_url);
      }
    }
  }
}
